import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import {
  formatSocialLinkText,
  getSocialProfileUrl,
  prepareAvailability,
  prepareNoticePeriod,
  toSafeHttpUrl,
} from "components/UserCardList/helpers/userCard";

import {
  EDUCATION_DEGREE_TYPES_TEXT,
  PROJECT_CASE_STUDY_WORK_TYPES_TEXT,
  SOCIAL_LINK_TYPES,
  TK_SKILL_CATEGORY_MAP,
} from "lookup";

import { JobsContext, SearchContext } from "context/providers";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import SvgIcon from "components/base/SvgIcon";
import SkillBadge from "components/SkillBadge";
import MatchedJobs from "./MatchedJobs";
import api from "apiSingleton";
import { placementIsActive } from "utils/validation";
import { customComparator, getGroupedSkillsByCategory } from "utils/common";
import ParsedResume from "./ParsedResume";
import { getPurifiedParsedMarkDown } from "utils/markdown";
import CollapsibleAssessments from "./CollapsibleAssessments";
import { getSearchStateQuery } from "utils/helpers/search";
import { cn } from "utils";
import LogoAvatar from "components/LogoAvatar";
import CareerPlaceholder from "components/CareerPlaceholder";
import {
  PROFILE_SECTION_TITLE_CLASSNAME,
  PROFILE_MORE_LINK_CLASSNAME,
  PROFILE_CAREER_TITLE_CLASSNAME,
  PROFILE_CAREER_META_CLASSNAME,
  PROFILE_CAREER_DESCRIPTION_CLASSNAME,
  PROFILE_PANEL_CLASSNAME,
  PROFILE_TEXT_SM,
  sortSkillsByExperience,
} from "../constants";
import { ProfileMoreLinkLabel } from "../ProfileMoreLinkLabel";
import {
  AtSign,
  Copy,
  Facebook,
  FileText,
  Github,
  Hash,
  Instagram,
  Linkedin,
  Link as LinkIcon,
  Phone,
  SquareArrowOutUpRight,
} from "lucide-react";
import { copyToClipboard } from "utils/copyToClipboard";
import { getCompanyDomain } from "utils/getCompanyDomain";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";

const DEFAULT_VISIBLE_SECTION_ITEMS = 3;
const normalizeDescriptionHtml = (html) =>
  html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>\s*<p[^>]*>/gi, " ")
    .replace(/<\/?p[^>]*>/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const CollapsedDescription = ({ html }) => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (!html) {
      container.textContent = "";
      return;
    }

    const normalizedHtml = normalizeDescriptionHtml(
      getPurifiedParsedMarkDown(html)
    );
    container.innerHTML = normalizedHtml;

    if (container.scrollHeight <= container.clientHeight) {
      return;
    }

    const words = (container.textContent || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) {
      return;
    }

    const fits = (wordCount) => {
      container.textContent = `${words.slice(0, wordCount).join(" ")}...`;
      return container.scrollHeight <= container.clientHeight;
    };

    let low = 1;
    let high = words.length - 1;
    let bestFitWordCount = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);

      if (fits(mid)) {
        bestFitWordCount = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    container.textContent = `${words
      .slice(0, Math.max(bestFitWordCount, 1))
      .join(" ")}...`;
  }, [html]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "min-w-0 max-h-10 overflow-hidden break-words",
        PROFILE_CAREER_DESCRIPTION_CLASSNAME
      )}
    />
  );
};

const formatYearRange = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = endDate ? dayjs(endDate) : null;

  if (!start.isValid() && !end?.isValid()) {
    return "-";
  }

  const startText = start.isValid() ? start.format("YYYY") : "-";
  const endText = end?.isValid() ? end.format("YYYY") : "Present";

  return `${startText} - ${endText}`;
};

const EmptyState = ({ children }) => (
  <div className="min-w-0 break-words rounded-lg bg-slate-50 px-4 py-5 text-sm text-slate-500">
    {children}
  </div>
);

const DetailItem = ({ children }) => (
  <p className={cn("flex min-w-0 gap-2 break-words", PROFILE_TEXT_SM)}>
    <span className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-slate-400" />
    <span className="min-w-0">{children}</span>
  </p>
);

const tableCellClassName = "min-w-0 break-words px-4 py-3";

const contactIconClassName = "h-4 w-4 shrink-0 text-slate-600";

const getSocialIcon = (type) => {
  switch (type) {
    case SOCIAL_LINK_TYPES.INSTAGRAM:
      return <Instagram className={contactIconClassName} />;
    case SOCIAL_LINK_TYPES.FACEBOOK:
      return <Facebook className={contactIconClassName} />;
    case SOCIAL_LINK_TYPES.LINKEDIN:
      return <Linkedin className={contactIconClassName} />;
    case SOCIAL_LINK_TYPES.GITHUB:
      return <Github className={contactIconClassName} />;
    case SOCIAL_LINK_TYPES.HASHNODE:
      return <Hash className={contactIconClassName} />;
    case SOCIAL_LINK_TYPES.PORTFOLIO:
      return <FileText className={contactIconClassName} />;
    case SOCIAL_LINK_TYPES.STACKOVERFLOW:
      return (
        <SvgIcon
          type="stackOverflow"
          className="h-4 w-4 shrink-0 fill-slate-600"
        />
      );
    default:
      return <LinkIcon className={contactIconClassName} />;
  }
};

const ContactItem = ({ icon, label, href, onAction, actionLabel, title }) => {
  const labelContent = (
    <>
      {icon}
      <span className="min-w-0 truncate">{label}</span>
    </>
  );
  const hasOpenAction = Boolean(href);
  const hasCopyAction = Boolean(onAction);

  return (
    <div className={cn("flex min-w-0 items-center gap-3", PROFILE_TEXT_SM)}>
      {hasOpenAction ? (
        <a
          className="flex min-w-0 items-center gap-2 text-inherit transition hover:text-sky-600"
          href={href}
          target="_blank"
          rel="noreferrer"
          title={title || label}
        >
          {labelContent}
        </a>
      ) : (
        <span
          className="flex min-w-0 items-center gap-2 text-inherit"
          title={title || label}
        >
          {labelContent}
        </span>
      )}

      {hasOpenAction ? (
        <a
          className="inline-flex shrink-0 items-center justify-center transition hover:text-sky-600"
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={actionLabel || `Open ${label}`}
          title={actionLabel || `Open ${label}`}
        >
          <SquareArrowOutUpRight className="h-4 w-4 shrink-0 text-slate-600" />
        </a>
      ) : null}

      {hasCopyAction ? (
        <button
          type="button"
          className="inline-flex shrink-0 items-center justify-center transition hover:text-sky-600"
          onClick={onAction}
          aria-label={actionLabel || `Copy ${label}`}
          title={actionLabel || `Copy ${label}`}
        >
          <Copy className="h-4 w-4 shrink-0 text-slate-600" />
        </button>
      ) : null}
    </div>
  );
};

const getSocialLinkByType = (hit, type) =>
  hit.socialLinks?.find((link) => link.type === type && link.value);

const buildPrimaryContactItems = (hit) => {
  const items = [];

  if (hit.email) {
    items.push({
      key: "email",
      icon: <AtSign className={contactIconClassName} />,
      label: hit.email,
      onAction: () => copyToClipboard(hit.email, "Email"),
      actionLabel: CTA_TITLES.copyEmail,
    });
  }

  if (hit.phone?.number) {
    items.push({
      key: "phone",
      icon: <Phone className={contactIconClassName} />,
      label: hit.phone.number,
      onAction: () => copyToClipboard(hit.phone.number, "Phone number"),
      actionLabel: CTA_TITLES.copyPhone,
    });
  }

  const linkedIn = getSocialLinkByType(hit, SOCIAL_LINK_TYPES.LINKEDIN);
  if (linkedIn) {
    items.push({
      key: "linkedin",
      icon: getSocialIcon(SOCIAL_LINK_TYPES.LINKEDIN),
      label: formatSocialLinkText(SOCIAL_LINK_TYPES.LINKEDIN),
      href: getSocialProfileUrl(SOCIAL_LINK_TYPES.LINKEDIN, linkedIn.value),
      actionLabel: "Open LinkedIn",
    });
  }

  if (hit.phone?.whatsAppAllowed && hit.phone?.number) {
    items.push({
      key: "whatsapp",
      icon: (
        <SvgIcon type="whatsapp" className="h-4 w-4 shrink-0 fill-slate-600" />
      ),
      label: "WhatsApp",
      href: `whatsapp://send?phone=${hit.phone.number}`,
      actionLabel: "Open WhatsApp",
    });
  }

  const github = getSocialLinkByType(hit, SOCIAL_LINK_TYPES.GITHUB);
  if (github) {
    items.push({
      key: "github",
      icon: getSocialIcon(SOCIAL_LINK_TYPES.GITHUB),
      label: formatSocialLinkText(SOCIAL_LINK_TYPES.GITHUB),
      href: getSocialProfileUrl(SOCIAL_LINK_TYPES.GITHUB, github.value),
      actionLabel: "Open GitHub",
    });
  }

  return items;
};

const SECONDARY_SOCIAL_TYPES = [
  SOCIAL_LINK_TYPES.INSTAGRAM,
  SOCIAL_LINK_TYPES.FACEBOOK,
  SOCIAL_LINK_TYPES.PORTFOLIO,
  SOCIAL_LINK_TYPES.HASHNODE,
  SOCIAL_LINK_TYPES.STACKOVERFLOW,
];

const buildSecondaryContactItems = (hit) =>
  SECONDARY_SOCIAL_TYPES.map((type) => {
    const socialLink = getSocialLinkByType(hit, type);
    if (!socialLink) {
      return null;
    }

    const label = formatSocialLinkText(type);
    const href = getSocialProfileUrl(type, socialLink.value);

    return {
      key: `social-${type}-${socialLink.value}`,
      icon: getSocialIcon(type),
      label,
      ...(href ? { href } : {}),
      actionLabel: `Open ${label}`,
    };
  }).filter(Boolean);

const ContactItemsGrid = ({ primaryItems, secondaryItems }) => {
  if (!primaryItems.length && !secondaryItems.length) {
    return <span className="text-slate-500">No contact links found.</span>;
  }

  return (
    <div className="grid min-h-0 w-full gap-6 sm:grid-cols-2 sm:gap-8">
      <div className="flex min-w-0 flex-col gap-3">
        {primaryItems.map((item) => (
          <ContactItem key={item.key} {...item} />
        ))}
      </div>
      {secondaryItems.length > 0 && (
        <div className="flex min-w-0 flex-col gap-3">
          {secondaryItems.map((item) => (
            <ContactItem key={item.key} {...item} />
          ))}
        </div>
      )}
    </div>
  );
};

const ViewDetailsLink = ({ href, label = "View details", className }) => {
  const safeHref = toSafeHttpUrl(href);

  if (!safeHref) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-2 whitespace-nowrap font-semibold text-slate-700",
          className
        )}
      >
        {label}
      </span>
    );
  }

  return (
    <a
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap font-semibold text-slate-700 transition hover:text-sky-600",
        className
      )}
      href={safeHref}
      target="_blank"
      rel="noreferrer"
    >
      {label}
      <SquareArrowOutUpRight className="h-4 w-4" />
    </a>
  );
};

ViewDetailsLink.propTypes = {
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
};

const renderSkillBadge = (skill, keyPrefix = "") => (
  <SkillBadge
    key={`${keyPrefix}${skill.name}-${skill.category}`}
    skillName={skill.name}
    category={skill.category}
    experience={skill.experience}
    noUppercase
    size="sm"
  />
);

const SkillTags = ({ skills, limit = 2 }) => {
  if (!skills?.length) {
    return <span className="text-slate-400">-</span>;
  }

  const sortedSkills = sortSkillsByExperience(skills);
  const visibleSkills = sortedSkills.slice(0, limit);
  const hiddenCount = Math.max(sortedSkills.length - visibleSkills.length, 0);

  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-2">
      {visibleSkills.map((skill) => renderSkillBadge(skill))}
      {hiddenCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex h-6 cursor-default items-center rounded-md bg-slate-100 px-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
              aria-label={`${hiddenCount} more skills`}
            >
              +{hiddenCount}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="flex max-w-[280px] flex-wrap gap-1.5">
              {sortedSkills.map((skill) => renderSkillBadge(skill, "tip-"))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

SkillTags.propTypes = {
  skills: PropTypes.arrayOf(PropTypes.object),
  limit: PropTypes.number,
};

const MoreButton = ({ count, expanded, onClick }) => {
  if (count <= 0) {
    return null;
  }

  return (
    <button
      type="button"
      className={PROFILE_MORE_LINK_CLASSNAME}
      onClick={onClick}
    >
      {expanded ? "Less" : <ProfileMoreLinkLabel count={count} />}
      <SvgIcon
        type="chevronDown"
        className={cn("h-4 w-4 text-slate-600 transition-transform", {
          "rotate-180": expanded,
        })}
      />
    </button>
  );
};

const LimitedSection = ({
  items,
  children,
  initialCount = DEFAULT_VISIBLE_SECTION_ITEMS,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = Math.max((items?.length || 0) - initialCount, 0);
  const visibleItems = expanded ? items : items.slice(0, initialCount);

  return (
    <>
      {visibleItems.map(children)}
      <MoreButton
        count={hiddenCount}
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      />
    </>
  );
};

const LimitedTableSection = ({
  items,
  children,
  initialCount = DEFAULT_VISIBLE_SECTION_ITEMS,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = Math.max((items?.length || 0) - initialCount, 0);
  const visibleItems = expanded ? items : items.slice(0, initialCount);

  return (
    <>
      {children(visibleItems)}
      <MoreButton
        count={hiddenCount}
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      />
    </>
  );
};

const ProfileSection = ({ title, children, action, className }) => (
  <section
    className={cn("talent-card-profile-section min-w-0 max-w-full", className)}
  >
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className={PROFILE_SECTION_TITLE_CLASSNAME}>{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const CareerChapterCard = ({
  item,
  company,
  title,
  description,
  stack,
  skills,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const hasDescription = Boolean(description);
  const safeLink = toSafeHttpUrl(item.link);
  const descriptionClassName =
    "prose max-w-full break-words [&_*]:max-w-full [&_a]:break-all [&_code]:break-all [&_pre]:overflow-x-auto";

  return (
    <div className="rounded-lg bg-slate-50 p-4">
      <div
        role={hasDescription ? "button" : undefined}
        tabIndex={hasDescription ? 0 : undefined}
        onClick={
          hasDescription
            ? () => setIsDescriptionExpanded((prev) => !prev)
            : undefined
        }
        onKeyDown={
          hasDescription
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsDescriptionExpanded((prev) => !prev);
                }
              }
            : undefined
        }
        className={cn(
          "grid min-w-0 items-center gap-4",
          hasDescription && "cursor-pointer",
          !hasDescription && "grid-cols-[44px_minmax(0,1fr)]",
          hasDescription &&
            !isDescriptionExpanded &&
            "grid-cols-[44px_minmax(160px,260px)_minmax(0,1fr)_20px]",
          hasDescription &&
            isDescriptionExpanded &&
            "grid-cols-[44px_minmax(0,1fr)_20px]"
        )}
      >
        <LogoAvatar
          name={company}
          domain={getCompanyDomain(item)}
          size="sm"
          placeHolder={<CareerPlaceholder size="sm" />}
        />

        <div className="min-w-0">
          <p className={cn("break-words", PROFILE_CAREER_TITLE_CLASSNAME)}>
            {title}
          </p>
          <p
            className={cn(
              "mt-1 flex flex-nowrap items-center gap-x-2",
              PROFILE_CAREER_META_CLASSNAME
            )}
          >
            <span className="min-w-0 truncate">{company}</span>
            <span className="text-slate-300">·</span>
            <span className="whitespace-nowrap">
              {formatYearRange(item.startDate, item.endDate)}
            </span>
          </p>
        </div>

        {hasDescription && !isDescriptionExpanded && (
          <CollapsedDescription html={description} />
        )}

        {hasDescription && (
          <span
            className="flex h-6 w-5 items-center justify-center self-center text-slate-500 transition hover:text-slate-700"
            aria-hidden="true"
          >
            <SvgIcon
              type="chevronDown"
              className={cn("h-4 w-4 text-current transition-transform", {
                "rotate-180": isDescriptionExpanded,
              })}
            />
          </span>
        )}
      </div>

      {hasDescription && isDescriptionExpanded && (
        <div
          className={cn(
            "mt-4 min-w-0 break-words pl-4 [&_*]:max-w-full [&_a]:break-all [&_code]:break-all [&_p]:break-words [&_pre]:overflow-x-auto",
            PROFILE_CAREER_DESCRIPTION_CLASSNAME,
            "[&_*]:font-inter [&_*]:text-sm [&_*]:font-normal [&_*]:text-gray-500"
          )}
        >
          <p
            className={descriptionClassName}
            dangerouslySetInnerHTML={{
              __html: getPurifiedParsedMarkDown(description),
            }}
          />
        </div>
      )}

      {!hasDescription && (
        <p className={cn("mt-3", PROFILE_CAREER_DESCRIPTION_CLASSNAME)}>
          No description available.
        </p>
      )}

      {(stack || skills || item.link) && (
        <div className="mt-3 min-w-0 break-words text-slate-600 [&_*]:max-w-full [&_a]:break-all [&_code]:break-all [&_p]:break-words [&_pre]:overflow-x-auto">
          {stack ? (
            <p className="line-clamp-2 break-words">
              <span className="font-semibold text-slate-700">Stack: </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: getPurifiedParsedMarkDown(stack),
                }}
              />
            </p>
          ) : null}
          {skills ? (
            <p className="line-clamp-2 break-words">
              <span className="font-semibold text-slate-700">Skills: </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: getPurifiedParsedMarkDown(skills),
                }}
              />
            </p>
          ) : null}
          {item.link && safeLink ? (
            <a
              className="inline-flex items-center gap-1 text-sky-500 underline"
              href={safeLink}
              target="_blank"
              rel="noreferrer"
            >
              View details
              <SvgIcon
                type={"GENERAL"}
                className="h-[15px] w-[15px] fill-sky-500"
              />
            </a>
          ) : null}
          {item.link && !safeLink ? (
            <span className="inline-flex items-center gap-1 text-slate-500">
              View details
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

const CollapsibleSection = ({
  activeTabName,
  hit,
  allowLocalHightLight,
  careerTrackAnalysis,
}) => {
  const [isResumeExpanded, setIsResumeExpanded] = useState(false);
  const [isLoadingPlacements] = useState(false);

  const { jobs } = useContext(JobsContext);
  const { searchState } = useContext(SearchContext);

  const searchStateQuery = getSearchStateQuery(searchState);

  function localHighlightBasedOnQuery(text, query, fullWords = true) {
    if (!query?.trim() || !allowLocalHightLight || !text) {
      return text || "";
    }

    // Match words inside quotes or single words outside of quotes
    let words = query.match(/"[^"]+"|[^,\s|;:/\\\-&_+]+/g) || []; // Match quoted phrases or single words

    words = words
      .map((word) => word.replace(/"/g, "")) // Remove double quotes
      .filter((word) => word.trim() !== ",") // Exclude the ',' symbol
      .map((word) => word.replace(/[.*#+?^${}()|[\]\\]/g, "\\$&"))
      .filter((e) => e); // Escape special regex characters

    // case insensitive
    const fullWordsType = fullWords
      ? `(\\b(${words.join("|")})\\b)`
      : `(${words.join("|")})`;
    const regex = new RegExp(fullWordsType, "gi");

    // Replace matched terms with highlighted versions using <mark>
    return text.replace(regex, "<mark>$1</mark>");
  }

  const assessments = useMemo(() => {
    return hit.assessments || [];
  }, [hit]);

  const educationRecords = useMemo(() => {
    const records = [...(hit.educationRecords || [])];

    return records.sort((a, b) => {
      return new Date(b.endDate) - new Date(a.endDate);
    });
  }, [hit]);

  const certifications = useMemo(() => {
    const records = [...(hit.certifications || [])];

    return records.sort((a, b) => {
      return new Date(b.issueDate) - new Date(a.issueDate);
    });
  }, [hit]);

  const projectsCaseStudies = useMemo(() => {
    return (hit.projectsCaseStudies || []).sort(customComparator);
  }, [hit]);

  const careers = useMemo(() => {
    return (hit.careers || []).sort(customComparator);
  }, [hit]);

  const resumeWorkHistory = useMemo(() => {
    return (hit.resumeWorkHistory || []).sort(customComparator);
  }, [hit]);

  const placements = useMemo(() => {
    return [...(hit.placements || [])].sort(
      (a, b) =>
        ((b.actualEndDate && new Date(b.actualEndDate)) ??
          new Date(b.endDate)) -
        ((a.actualEndDate && new Date(a.actualEndDate)) ?? new Date(a.endDate))
    );
  }, [hit.placements]);

  const JobTitle = ({ jobId }) => {
    const [jobTitle, setJobTitle] = useState("Loading...");

    useEffect(() => {
      (async () => {
        const job = jobs?.find(({ id }) => id === jobId);
        if (job?.title) {
          setJobTitle(job.title);
          return;
        }
        const { data } = await api.jobs.get({ id: jobId });
        setJobTitle(data.getJobOpportunity?.title || "-");
      })();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return jobTitle === "-" || jobTitle === "Loading..." ? (
      <p>{jobTitle}</p>
    ) : (
      <a target="_blank" rel="noreferrer" href={`/#/jobOpps/${jobId}/details`}>
        <p className="font-bold text-base text-sky-500 underline">{jobTitle}</p>
      </a>
    );
  };

  const renderPlacements = (items) => {
    return items.map((placement, index) => {
      return (
        <div
          className="flex flex-col font-inter font-medium text-sm w-full"
          key={placement.id}
        >
          {index > 0 && <hr className="border-t my-1 w-full" />}

          <div className="flex min-w-0 flex-wrap gap-2.5">
            <span className="min-w-[180px] flex-1 break-words">
              {placement.companyName ?? "-"}
            </span>

            <span className="min-w-[180px] flex-1">
              Start Date:{" "}
              {dayjs(placement.startDate)
                .format("MM DD YYYY")
                .split(" ")
                .join("/")}
            </span>

            <span
              className="min-w-[180px] flex-1 break-words"
              title={placement.actualEndDate ? "Actual End Date" : "End Date"}
            >
              End Date:{" "}
              {placement.actualEndDate
                ? dayjs(placement.actualEndDate)
                    .format("MM DD YYYY")
                    .split(" ")
                    .join("/")
                : dayjs(placement.endDate)
                    .format("MM DD YYYY")
                    .split(" ")
                    .join("/")}
              {placement.endReason && (
                <>
                  <br />
                  <span>End Reason: {placement.endReason}</span>
                </>
              )}
            </span>

            <span
              className={cn({
                "min-w-[180px] flex-1 text-center": !placement.jobOpportunityId,
              })}
            >
              {placementIsActive(placement.actualEndDate ?? placement.endDate)
                ? "Currently Engaged"
                : "Previous Engagement"}
            </span>

            {placement.jobOpportunityId && (
              <span className="min-w-[180px] flex-1 break-words">
                <JobTitle jobId={placement.jobOpportunityId} />
              </span>
            )}
          </div>
        </div>
      );
    });
  };

  const renderTorcRatings = (rating) => {
    const skillsByCategory = getGroupedSkillsByCategory(rating.TKSkills || []);

    return (
      <div className="space-y-3 rounded-lg bg-slate-50 p-4">
        {Object.keys(skillsByCategory).map((categoryKey) => (
          <div key={categoryKey}>
            <p className="mb-2 text-xs font-semibold uppercase text-slate-500">
              {TK_SKILL_CATEGORY_MAP[categoryKey]}
            </p>
            <div className="space-y-1">
              {skillsByCategory[categoryKey].map((skill) => (
                <div
                  className="grid grid-cols-[1fr_80px] text-sm text-slate-600"
                  key={`${skill.name}_${skill.category}`}
                >
                  <span>{skill.name}</span>
                  <span>{skill.rating * 100} %</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_80px] border-t border-slate-200 pt-2 text-sm font-semibold text-slate-700">
          <span>Overall</span>
          <span>{rating.overall * 100} %</span>
        </div>
      </div>
    );
  };

  const getHighlightResult = (highlightResult, sourceValue, childField) => {
    let fieldResult;

    if (highlightResult?.[childField]) {
      fieldResult = highlightResult?.[childField]?.find(
        (hc) =>
          hc?.matchLevel !== "none" &&
          sourceValue[childField] ===
            hc?.value.replace(/<\/?ais-highlight-0000000000>/g, "")
      );

      return fieldResult?.value
        ?.replace(/<ais-highlight-0000000000>/g, "<mark>")
        ?.replace(/<\/ais-highlight-0000000000>/g, "</mark>");
    } else if (Array.isArray(highlightResult)) {
      fieldResult = highlightResult?.find(
        (hc) =>
          hc[childField]?.matchLevel !== "none" &&
          sourceValue[childField] ===
            hc[childField]?.value.replace(/<\/?ais-highlight-0000000000>/g, "")
      );
    }

    return fieldResult?.[childField]?.value
      ?.replace(/<ais-highlight-0000000000>/g, "<mark>")
      ?.replace(/<\/ais-highlight-0000000000>/g, "</mark>");
  };

  const parsedResumeHasHighLight = useMemo(() => {
    if (searchState.basicSearch) return false;
    if (Array.isArray(hit?._highlightResult?.parsedResume)) {
      return hit?._highlightResult?.parsedResume?.some((hc) => {
        return hc.content && hc.content?.matchLevel !== "none";
      });
    }

    return hit?._highlightResult?.parsedResume?.content?.some((hc) => {
      return hc && hc.matchLevel !== "none";
    });
  }, [hit, searchState.basicSearch]);

  hit.parsedResume = useMemo(() => {
    if (!searchStateQuery) {
      return hit.parsedResume;
    }

    return hit.parsedResume?.map((e) => {
      const localHighlighted = localHighlightBasedOnQuery(
        e.content,
        searchStateQuery
      );

      return {
        ...e,
        localHighlighted,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit, searchStateQuery]);

  const resumeHasLocalHighLight =
    !searchState.basicSearch &&
    hit.parsedResume?.some(
      ({ localHighlighted }) =>
        localHighlighted && /<\/?mark>/g.test(localHighlighted)
    );

  const hitBio = useMemo(() => {
    let highlightedBio = "";

    if (
      !searchState.basicSearch &&
      hit._highlightResult?.bio?.matchLevel !== "none" &&
      hit.bio ===
        hit._highlightResult?.bio?.value.replace(
          /<\/?ais-highlight-0000000000>/g,
          ""
        )
    ) {
      highlightedBio = hit._highlightResult?.bio?.value
        ?.replace(/<ais-highlight-0000000000>/g, "<mark>")
        ?.replace(/<\/ais-highlight-0000000000>/g, "</mark>");
    }

    return (
      highlightedBio ||
      (hit.bio
        ? localHighlightBasedOnQuery(hit.bio, searchStateQuery)
        : "-" || "-")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit.bio, hit._highlightResult?.bio, searchStateQuery]);

  const renderChapter = (item, source) => {
    const highlightSource = hit._highlightResult?.[source];
    const highlightedDescription = getHighlightResult(
      highlightSource,
      item,
      "description"
    );
    const description =
      highlightedDescription ||
      (item.description
        ? localHighlightBasedOnQuery(item.description, searchStateQuery)
        : "");
    const isProjectChapter = source === "projectsCaseStudies";
    const stack = isProjectChapter
      ? getHighlightResult(highlightSource, item, "stack") ||
        (item.stack
          ? localHighlightBasedOnQuery(item.stack, searchStateQuery)
          : "")
      : "";
    const skills = isProjectChapter
      ? localHighlightBasedOnQuery(
          item.TKSkills?.map((e) => e.name).join(", ") || "",
          searchStateQuery
        )
      : "";
    const company =
      item.companyName ||
      item.client ||
      PROJECT_CASE_STUDY_WORK_TYPES_TEXT[item.workType] ||
      "-";
    const title = item.title || item.name || item.client || "Untitled";

    return (
      <CareerChapterCard
        key={`${source}-${item.id || title}-${item.startDate || ""}-${
          item.endDate || ""
        }-${company}`}
        item={item}
        company={company}
        title={title}
        description={description}
        stack={stack}
        skills={skills}
      />
    );
  };

  const getProjectSkills = (project) => {
    const tkSkills = project.TKSkills || [];

    if (tkSkills.length) {
      return tkSkills.map((skill) => skill.name).filter(Boolean);
    }

    return (project.stack || "")
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean);
  };

  const renderProjectSkillPills = (project) => {
    const skills = getProjectSkills(project);
    const visibleSkills = skills.slice(0, 2);
    const hiddenCount = Math.max(skills.length - visibleSkills.length, 0);

    if (!skills.length) {
      return <span className="text-slate-400">-</span>;
    }

    return (
      <div className="flex min-w-0 flex-wrap gap-2">
        {visibleSkills.map((skill) => (
          <span
            key={`${project.id}-${skill}`}
            className="inline-flex h-7 max-w-[140px] items-center truncate rounded-md bg-white px-2 text-xs font-semibold text-slate-700"
            title={skill}
          >
            {skill}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="inline-flex h-7 items-center rounded-md bg-white px-2 text-xs font-semibold text-slate-700">
            +{hiddenCount}
          </span>
        )}
      </div>
    );
  };

  const renderEducationRows = (visibleEducationRecords) => {
    const gridClassName =
      "grid min-w-0 grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-4";

    return (
      <div className="space-y-3">
        {visibleEducationRecords.map((item) => (
          <div
            key={item.id}
            className={cn(
              gridClassName,
              "rounded-lg bg-slate-50 text-sm text-slate-600"
            )}
          >
            <span className={tableCellClassName}>{item.school}</span>
            <span className={tableCellClassName}>
              {item.degreeType
                ? EDUCATION_DEGREE_TYPES_TEXT[item.degreeType] ??
                  item.degreeType
                : "-"}
            </span>
            <span className={tableCellClassName}>{item.degreeName || "-"}</span>
            <span className={tableCellClassName}>
              {item.fieldOfStudy || "-"}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderCertificationRows = (visibleCertifications) => {
    const gridClassName =
      "grid min-w-[900px] grid-cols-[52px_minmax(180px,1.4fr)_minmax(160px,1fr)_140px_minmax(200px,1.2fr)_120px] items-center gap-4";

    return (
      <div className="w-full overflow-x-auto">
        <div className="space-y-3">
          {visibleCertifications.map((item, index) => (
            <div
              key={
                item.id ||
                `${item.title ?? ""}-${item.issuingOrganization ?? ""}-${index}`
              }
              className={cn(
                gridClassName,
                "rounded-lg bg-slate-50 text-sm text-slate-600"
              )}
            >
              <span className={cn(tableCellClassName, "w-[52px]")}>
                <LogoAvatar
                  name={item.issuingOrganization}
                  domain={item.issuingOrganizationDomain}
                  size="sm"
                  fallBackInitial="C"
                  className="h-10 w-10 min-w-[40px]"
                />
              </span>
              <span className={tableCellClassName}>{item.title || "-"}</span>
              <span className={tableCellClassName}>
                {item.issuingOrganization || "-"}
              </span>
              <span className={cn(tableCellClassName, "whitespace-nowrap")}>
                {formatYearRange(item.issueDate, item.expirationDate)}
              </span>
              <span className={tableCellClassName}>
                <SkillTags
                  skills={(item.TKSkills ?? []).filter((skill) => skill?.name)}
                />
              </span>
              <span className={cn(tableCellClassName, "flex justify-end")}>
                {item.credentialUrl ? (
                  <ViewDetailsLink href={item.credentialUrl} />
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProjectRows = (visibleProjects) => {
    const gridClassName =
      "grid min-w-[900px] grid-cols-[minmax(220px,2fr)_180px_140px_minmax(220px,1.4fr)_130px] items-center";

    return (
      <div className="w-full overflow-x-auto">
        <div className="space-y-2">
          {visibleProjects.map((project) => {
            const title = project.title || project.name || "Untitled";
            const company =
              project.client ||
              PROJECT_CASE_STUDY_WORK_TYPES_TEXT[project.workType] ||
              "-";

            return (
              <div
                key={project.id || `${title}-${company}`}
                className={`${gridClassName} rounded-lg bg-slate-50 text-sm text-slate-600`}
              >
                <span
                  className={`${tableCellClassName} font-semibold text-slate-700`}
                  title={title}
                >
                  {title}
                </span>
                <span className={tableCellClassName} title={company}>
                  {company}
                </span>
                <span className={tableCellClassName}>
                  {formatYearRange(project.startDate, project.endDate)}
                </span>
                <span className={tableCellClassName}>
                  {renderProjectSkillPills(project)}
                </span>
                <span className={`${tableCellClassName} flex justify-end`}>
                  {project.link ? (
                    <ViewDetailsLink href={project.link} />
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const careerChapters = [
    ...careers.map((career) => ({ ...career, _source: "careers" })),
    ...resumeWorkHistory.map((entry) => ({
      ...entry,
      _source: "resumeWorkHistory",
    })),
  ].sort(customComparator);
  const primaryContactItems = useMemo(
    () => buildPrimaryContactItems(hit),
    [hit]
  );
  const secondaryContactItems = useMemo(
    () => buildSecondaryContactItems(hit),
    [hit]
  );
  const hasContactItems =
    primaryContactItems.length > 0 || secondaryContactItems.length > 0;
  const worksRemotely =
    hit.worksRemotely ??
    hit.worksRemote ??
    hit.remoteWork ??
    hit.isRemote ??
    null;
  const worksRemotelyLabel =
    worksRemotely === true
      ? "Yes"
      : worksRemotely === false
      ? "No"
      : "Not Specified";

  return (
    <div
      id="talent-card-collapsible-section-group"
      className="mt-6 flex w-full min-w-0 max-w-full flex-col gap-8 overflow-x-hidden"
    >
      <ProfileSection title="Career Chapters">
        <div className="mt-3 space-y-3">
          {careerChapters.length ? (
            <LimitedSection items={careerChapters}>
              {(item) => renderChapter(item, item._source)}
            </LimitedSection>
          ) : (
            <EmptyState>No career history has been added yet.</EmptyState>
          )}
        </div>
      </ProfileSection>

      {careerTrackAnalysis}

      {hit.bio && (
        <ProfileSection title="Bio">
          <div className="rounded-lg bg-surface p-[18px]">
            <p className="text-md font-bold">{hit.tagline}</p>
            <p
              className="prose break-words"
              dangerouslySetInnerHTML={{
                __html: getPurifiedParsedMarkDown(hitBio),
              }}
            />
          </div>
        </ProfileSection>
      )}

      <div
        className={cn(
          "grid gap-10 lg:items-stretch",
          hasContactItems && "lg:grid-cols-2"
        )}
      >
        {hasContactItems && (
          <div className="flex h-full flex-col">
            <ProfileSection title="Contact" className="flex h-full flex-col">
              <div
                className={cn(PROFILE_PANEL_CLASSNAME, "flex h-full flex-1")}
              >
                <ContactItemsGrid
                  primaryItems={primaryContactItems}
                  secondaryItems={secondaryContactItems}
                />
              </div>
            </ProfileSection>
          </div>
        )}

        <div className="flex h-full flex-col">
          <ProfileSection title="Details" className="flex h-full flex-col">
            <div
              className={cn(
                PROFILE_PANEL_CLASSNAME,
                "flex h-full flex-1 flex-col gap-3"
              )}
            >
              <DetailItem>
                Availability: {prepareAvailability(hit.availability)}
              </DetailItem>
              <DetailItem>
                Notice Period: {prepareNoticePeriod(hit.noticePeriod)}
              </DetailItem>
              <DetailItem>Works remotely: {worksRemotelyLabel}</DetailItem>
              <DetailItem>
                Member since:{" "}
                {hit.createdAt
                  ? dayjs(hit.createdAt).format("MM/DD/YYYY")
                  : "Not Specified"}
              </DetailItem>
            </div>
          </ProfileSection>
        </div>
      </div>

      <CollapsibleAssessments assessments={assessments} hit={hit} />

      {educationRecords.length > 0 && (
        <ProfileSection title="Education">
          <LimitedTableSection items={educationRecords}>
            {(visibleEducationRecords) =>
              renderEducationRows(visibleEducationRecords)
            }
          </LimitedTableSection>
        </ProfileSection>
      )}

      {certifications.length > 0 && (
        <ProfileSection title="Certifications">
          <TooltipProvider delayDuration={100}>
            <LimitedTableSection items={certifications}>
              {(visibleCertifications) =>
                renderCertificationRows(visibleCertifications)
              }
            </LimitedTableSection>
          </TooltipProvider>
        </ProfileSection>
      )}

      <MatchedJobs hit={hit} activeTabName={activeTabName} key={hit.id} />

      {!!hit.ratings?.torc && (
        <ProfileSection title="Torc Ratings">
          {renderTorcRatings(hit.ratings.torc)}
        </ProfileSection>
      )}

      {projectsCaseStudies.length > 0 && (
        <ProfileSection title="Projects">
          <div className="space-y-3">
            <LimitedTableSection items={projectsCaseStudies}>
              {(visibleProjects) => renderProjectRows(visibleProjects)}
            </LimitedTableSection>
          </div>
        </ProfileSection>
      )}

      {!isLoadingPlacements && placements.length > 0 && (
        <ProfileSection title="Placements">
          <LimitedSection items={placements}>
            {(placement) => renderPlacements([placement])}
          </LimitedSection>
        </ProfileSection>
      )}

      {hit.parsedResume?.length > 0 && (
        <ProfileSection
          title="Resume Text"
          action={
            <button
              type="button"
              className="flex h-6 w-5 items-center justify-center text-slate-500 transition hover:text-slate-700"
              onClick={() => setIsResumeExpanded((prev) => !prev)}
              aria-expanded={isResumeExpanded}
              aria-label={
                isResumeExpanded ? "Collapse resume text" : "Expand resume text"
              }
            >
              <SvgIcon
                type="chevronDown"
                className={cn("h-4 w-4 text-current transition-transform", {
                  "rotate-180": isResumeExpanded,
                })}
              />
            </button>
          }
        >
          {isResumeExpanded && (
            <ParsedResume
              hit={hit}
              parsedResumeHasHighLight={parsedResumeHasHighLight}
              resumeHasLocalHighLight={resumeHasLocalHighLight}
              localHighlightBasedOnQuery={localHighlightBasedOnQuery}
              hideHighlights
            />
          )}
        </ProfileSection>
      )}
    </div>
  );
};

CollapsibleSection.propTypes = {
  hit: PropTypes.object,
  activeTabName: PropTypes.string,
  careerTrackAnalysis: PropTypes.node,
};

CollapsibleSection.defaultPropt = {
  hit: {},
  activeTabName: "SEARCHRESULTS",
};

export default CollapsibleSection;
